/**
 * TASK-30 — the ONE web-side home for the exhaustive `FtErrorCode` -> string map (review
 * follow-up: previously duplicated verbatim in `SendFileCard.tsx` and `ReceiveFileCard.tsx`).
 * Both cards show identical copy for a given code — the failure means the same thing regardless
 * of which card is showing it. Locale strings stay canonical in `i18n/messages.{en,es}.ts`
 * (`fileTransfer.error*`); this module owns only the code -> key mapping.
 *
 * `Record<FtErrorCode, string>` requires every union member as a key, so a future added
 * `FtErrorCode` fails to compile here until this map is updated — the "satisfies-never"
 * exhaustiveness guarantee for this platform family.
 */
import type { FtErrorCode } from "@reflecto/core";
import { messages } from "../i18n";

export const FT_ERROR_MESSAGES: Record<FtErrorCode, string> = {
  CHUNK_AUTH_FAIL: messages.fileTransfer.errorChunkAuthFail,
  HASH_MISMATCH: messages.fileTransfer.errorHashMismatch,
  STORAGE_FULL: messages.fileTransfer.errorStorageFull,
  EXPIRED: messages.fileTransfer.errorExpired,
  QUOTA_EXCEEDED: messages.fileTransfer.errorQuotaExceeded,
  INTERNAL: messages.fileTransfer.errorInternal,
};

export function isFtErrorCode(code: string): code is FtErrorCode {
  // Own-keys membership — a plain `in` on the object literal would also match
  // prototype keys like "toString" for a future unvalidated caller.
  return Object.keys(FT_ERROR_MESSAGES).includes(code);
}

/**
 * The code-specific string for `code` when it is one of the 6 wire `FtErrorCode`s, else
 * `undefined` — callers append their own per-card fallback (`?? messages....`), which is the one
 * piece that legitimately differs between the send and receive cards.
 */
export function ftErrorMessage(code: string | undefined): string | undefined {
  return code !== undefined && isFtErrorCode(code) ? FT_ERROR_MESSAGES[code] : undefined;
}
